import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";
import styles from "../../styles";
import FirstPageHeader from "../FirstPageHeader";
import { roundedCornerStyle, roundedRowStyle } from "../../helpers";
import {
    formatDisplayValue,
    formatLinkedLegalEntity,
    formatPersonName,
    resolveCurrentUser,
} from "../../helpers";

const CoverPage = ({
    t,
    supplierDetail = {},
    user,
    downloadDate,
    logo,
    primaryColor,
    startColor,
    stopColor,
    isOwnCompany = false,
}) => {
    const linkedLegalEntity = formatLinkedLegalEntity(supplierDetail);
    const currentUser = resolveCurrentUser(user);

    const metaCells = [
        {
            label: "Assigned Analyst",
            value: formatPersonName(supplierDetail?.assignedAnalyst),
        },
        {
            label: "Report Download Date",
            value: downloadDate,
        },
        {
            label: "Downloaded By",
            value: formatPersonName(currentUser),
        },
        {
            label: "Report ID",
            value: supplierDetail?.id,
        },
    ];

    if (linkedLegalEntity) {
        metaCells.push({
            label: "Linked Legal Entities",
            value: linkedLegalEntity,
            fullWidth: true,
        });
    }

    const metaRows = [];
    let cellIndex = 0;
    while (cellIndex < metaCells.length) {
        const cell = metaCells[cellIndex];
        if (cell.fullWidth) {
            metaRows.push([cell]);
            cellIndex += 1;
        } else {
            metaRows.push(metaCells.slice(cellIndex, cellIndex + 2));
            cellIndex += 2;
        }
    }

    return (
        <View style={styles.coverPage}>
            <FirstPageHeader
                backgroundColor={primaryColor}
                logo={logo}
                startColor={startColor || primaryColor}
                stopColor={stopColor || primaryColor}
                isOwnCompany={isOwnCompany}
            />

            <View style={styles.coverContent}>
                <Text style={styles.coverEyebrow}>
                    {t("Third Party Due Diligence").toUpperCase()}
                </Text>

                <Text style={styles.coverMainTitle}>
                    {t("Due Diligence Report")}
                </Text>

                <Text style={styles.coverMainSubtitle}>
                    {t("Company intelligence, external information and risk review")}
                </Text>

                <View style={styles.coverAccentLine} />

                <Text style={styles.coverCompanyName}>
                    {formatDisplayValue(supplierDetail?.name)}
                </Text>

                <Text style={styles.coverCompanySubtitle}>
                    {t("Management summary and company information")}
                </Text>

                <View style={styles.coverMetaGrid}>
                    {metaRows.map((row, rowIndex) => {
                        const isLastRow = rowIndex === metaRows.length - 1;
                        const isAltRow = rowIndex % 2 === 1;

                        return (
                            <View
                                key={`cover-meta-row-${rowIndex}`}
                                style={{
                                    ...styles.coverMetaRow,
                                    ...(isLastRow ? styles.coverMetaRowLast : {}),
                                    ...roundedRowStyle({
                                        isFirstRow: rowIndex === 0,
                                        isLastRow,
                                        radius: 8,
                                    }),
                                }}
                            >
                                {row.map((cell, cellIndex) => {
                                    const isLastInRow = cellIndex === row.length - 1;

                                    return (
                                        <View
                                            key={cell.label}
                                            style={{
                                                ...styles.coverMetaCell,
                                                ...(isLastInRow ? styles.coverMetaCellLast : {}),
                                                ...(cell.fullWidth || row.length === 1
                                                    ? { width: "100%" }
                                                    : {}),
                                                ...(isAltRow ? styles.coverMetaCellAlt : {}),
                                                ...roundedCornerStyle({
                                                    isFirstRow: rowIndex === 0,
                                                    isLastRow,
                                                    isFirstCol: cellIndex === 0,
                                                    isLastCol: isLastInRow || cell.fullWidth || row.length === 1,
                                                    radius: 8,
                                                }),
                                            }}
                                        >
                                            <Text style={styles.coverMetaLabel}>
                                                {t(cell.label).toUpperCase()}
                                            </Text>
                                            <Text style={styles.coverMetaValue}>
                                                {formatDisplayValue(cell.value)}
                                            </Text>
                                        </View>
                                    );
                                })}
                            </View>
                        );
                    })}
                </View>
            </View>
        </View>
    );
};

export default withNamespaces()(CoverPage);
